import { CUSTOMER_DATA_MAX_FILE_BYTES, CUSTOMER_DATA_TYPES, isAllowedDataFile } from '@harness/shared/customer-data';
import type { DataFile, DataListing } from '@harness/shared/protocol';
import { useRef, useState } from 'react';
import { dataFileUrl, type DataUploadResult, uploadDataFile } from '../api';
import appStyles from '../styles/app.module.css';
import styles from '../styles/data.module.css';
import { dataUploadErrorText, sectionHeader, TEXT } from '../texts';
import { Avatar } from './Avatar';
import { Confirm } from './Confirm';
import { TrashIcon } from './icons';
import { PageHeader } from './PageHeader';

// Month as a name ("31 Oct 2026"), unambiguous for every reader.
const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const NUMBER = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const ALLOWED = Object.keys(CUSTOMER_DATA_TYPES);

export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${NUMBER.format(unit === 0 ? value : Math.round(value * 10) / 10)} ${units[unit]}`;
}

export type UploadDataFn = (projectId: string, file: File, replace: boolean, onProgress?: (fraction: number) => void) => Promise<DataUploadResult>;

interface DataProps {
  projectId: string;
  /** null while loading. */
  listing: DataListing | null | 'failed';
  /** Reload the list; the socket announces changes of other consultants. */
  onChanged: () => void;
  onDelete: (name: string) => Promise<boolean>;
  /** Tests pass a fake upload. */
  upload?: UploadDataFn;
}

interface Running {
  id: number;
  name: string;
  progress: number;
}

/** The customer data (#11): a flat list of files all consultants of the customer share. */
export function Data({ projectId, listing, onChanged, onDelete, upload = uploadDataFile }: DataProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [running, setRunning] = useState<Running[]>([]);
  // Uploads whose name exists already; each one asks "Replace?" in turn.
  const [toReplace, setToReplace] = useState<File[]>([]);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);

  const left = listing === null || listing === 'failed' ? '–' : formatBytes(Math.max(0, listing.quotaBytes - listing.usedBytes));

  const send = async (file: File, replace: boolean): Promise<void> => {
    if (!isAllowedDataFile(file.name)) return setNotice(dataUploadErrorText('type', file.name, ALLOWED, left));
    if (file.size > CUSTOMER_DATA_MAX_FILE_BYTES) return setNotice(dataUploadErrorText('too-large', file.name, ALLOWED, left));
    const id = nextId.current;
    nextId.current += 1;
    setRunning((current) => [...current, { id, name: file.name, progress: 0 }]);
    const result = await upload(projectId, file, replace, (progress) =>
      setRunning((current) => current.map((item) => (item.id === id ? { ...item, progress } : item))),
    ).catch((): DataUploadResult => ({ ok: false, error: 'failed' }));
    setRunning((current) => current.filter((item) => item.id !== id));
    if (result.ok) {
      setNotice(TEXT.dataUploaded(result.name));
      onChanged();
    } else if (result.error === 'exists' && !replace) {
      setToReplace((current) => [...current, file]);
    } else {
      setNotice(dataUploadErrorText(result.error, file.name, ALLOWED, left));
    }
  };

  const remove = async (name: string): Promise<void> => {
    setToDelete(null);
    const ok = await onDelete(name).catch(() => false);
    setNotice(ok ? TEXT.dataDeleted(name) : TEXT.dataDeleteFailed(name));
  };

  const header = (
    <PageHeader
      {...sectionHeader.data}
      actions={
        <>
          <button type="button" className={appStyles.headerButton} onClick={() => input.current?.click()}>
            {TEXT.uploadData}
          </button>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            accept={ALLOWED.join(',')}
            data-testid="data-input"
            onChange={(event) => {
              for (const file of Array.from(event.target.files ?? [])) void send(file, false);
              event.target.value = '';
            }}
          />
        </>
      }
    />
  );

  if (listing === null || listing === 'failed') {
    return (
      <>
        {header}
        <main className={styles.main}>
          <p className={styles.muted}>{listing === null ? TEXT.loading : TEXT.loadFailed}</p>
        </main>
      </>
    );
  }

  const needle = query.trim().toLowerCase();
  const shown = needle === '' ? listing.files : listing.files.filter((file) => file.name.toLowerCase().includes(needle));
  const people = new Set(listing.files.flatMap((file) => (file.uploadedBy === null ? [] : [file.uploadedBy])));
  const replacing = toReplace[0];

  return (
    <>
      {header}
      <main className={styles.main}>
        {notice !== null && (
          <div className={styles.notice} role="status">
            <span>{notice}</span>
            <button type="button" className={styles.close} aria-label={TEXT.closeNotice} onClick={() => setNotice(null)}>
              ×
            </button>
          </div>
        )}
        {running.map((item) => (
          <div key={item.id} className={styles.progress} role="status">
            <span>{TEXT.dataUploading(item.name, Math.round(item.progress * 100))}</span>
            <span className={styles.bar} aria-hidden="true">
              <span style={{ width: `${Math.round(item.progress * 100)}%` }} />
            </span>
          </div>
        ))}
        <section className={styles.stats}>
          <Stat label={TEXT.dataFiles} value={String(listing.files.length)} />
          <Stat label={TEXT.dataPeople} value={TEXT.dataPeopleCount(people.size)} />
          <Stat label={TEXT.dataStorage} value={TEXT.dataStorageOf(formatBytes(listing.usedBytes), formatBytes(listing.quotaBytes))} />
        </section>
        <label className={styles.search}>
          {TEXT.dataSearch}
          <input type="search" value={query} placeholder={TEXT.dataSearchPlaceholder} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{TEXT.dataFile}</th>
                <th>{TEXT.dataSize}</th>
                <th>{TEXT.dataUploadedBy}</th>
                <th>{TEXT.dataDate}</th>
                <th>
                  <span className={styles.hidden}>{TEXT.dataActions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((file) => (
                <Row key={file.name} projectId={projectId} file={file} onDelete={() => setToDelete(file.name)} />
              ))}
            </tbody>
          </table>
          {shown.length === 0 && <p className={styles.empty}>{listing.files.length === 0 ? TEXT.noDataFiles : TEXT.noDataMatches}</p>}
        </div>
      </main>
      {replacing !== undefined && (
        <Confirm
          title={TEXT.dataReplaceTitle(replacing.name)}
          text={TEXT.dataReplaceText}
          action={TEXT.dataReplace}
          onCancel={() => setToReplace((current) => current.slice(1))}
          onConfirm={() => {
            setToReplace((current) => current.slice(1));
            void send(replacing, true);
          }}
        />
      )}
      {toDelete !== null && (
        <Confirm title={TEXT.dataDeleteTitle(toDelete)} text={TEXT.dataDeleteText} action={TEXT.delete} onCancel={() => setToDelete(null)} onConfirm={() => void remove(toDelete)} />
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
    </div>
  );
}

function Row({ projectId, file, onDelete }: { projectId: string; file: DataFile; onDelete: () => void }): React.JSX.Element {
  return (
    <tr>
      <td>
        <span className={styles.fileCell}>
          <span className={styles.type}>{file.type}</span>
          <a className={styles.fileName} href={dataFileUrl(projectId, file.name)} download title={TEXT.dataDownload(file.name)}>
            {file.name}
          </a>
        </span>
      </td>
      <td className={styles.mutedCell}>{formatBytes(file.size)}</td>
      <td>
        {file.uploadedBy === null ? (
          <span className={styles.mutedCell}>{TEXT.dataUnknownPerson}</span>
        ) : (
          <span className={styles.person}>
            <Avatar name={file.uploadedBy} email={file.uploadedBy} />
            {file.uploadedBy}
          </span>
        )}
      </td>
      <td className={styles.mutedCell}>{DATE.format(file.uploadedAt)}</td>
      <td>
        <button type="button" className={styles.iconButton} aria-label={TEXT.dataDelete(file.name)} onClick={onDelete}>
          <TrashIcon />
        </button>
      </td>
    </tr>
  );
}
